import "features/robot-teleop/components/robot-status/status-ctx/robot-status.css";
import { Col, Progress, Row, Space, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { red, green } from "@ant-design/colors";
import BatteryIcon from "features/robot-teleop/components/robot-status/status/battery-status";

const formatNumber = (value, digits = 2) =>
  typeof value === "number" && Number.isFinite(value) ? value.toFixed(digits) : "?";

const formatVector = (vector) =>
  Array.isArray(vector) ? `[${vector.map((v) => formatNumber(v)).join(", ")}]` : "?";

// mission_info.current_task may be a plain string or a task object
const formatTask = (task) => {
  if (task == null) return null;
  if (typeof task === "string") return task;
  if (typeof task === "object") return task.name ?? task.id ?? JSON.stringify(task);
  return String(task);
};

// RobotInfo.robot_type is either the registered type's name or the robot_type document
const registeredTypeName = (robotType) => {
  if (robotType == null) return null;
  if (typeof robotType === "object") return robotType.name ?? null;
  return String(robotType);
};

/**
 * Presentational status panel of the control pages, fed with a normalized `robot.status`
 * message (features/robot-teleop/utils/robotStatus.js). It reads no React context, so it
 * works on the legacy RobotPeerConnectionProvider pages (through status-ctx/robot-status.jsx)
 * and on the ConnectionManager-based robot-control-page alike.
 *
 * @param {Object|null} props.RobotStatus - { robot_info, robot_state, mission_info, derived },
 *   or null before the first message
 * @param {Object} props.RobotInfo - identity shown in the panel: { robot_id, icon_path, name,
 *   robot_type?: string | { name } } — the model the robot reports in robot_info takes
 *   precedence over the registered type
 * @param {React.ReactNode} [props.ConnectionStatus] - optional connection widget rendered in
 *   its own column (the legacy pages pass RobotConnectionStatus)
 * @param {React.ReactNode} [props.children] - optional extra content rendered as a trailing
 *   column (e.g. RobotConnectedUsers)
 */
const RobotStatusPanel = ({ RobotStatus = null, RobotInfo = {}, ConnectionStatus = null, children = null }) => {
  const { t } = useTranslation();
  const robotInfo = RobotStatus?.robot_info;
  const robotState = RobotStatus?.robot_state;
  const missionInfo = RobotStatus?.mission_info;
  const derived = RobotStatus?.derived;

  const battery = robotState?.battery_state;
  const batteryTooltip = battery
    ? t("teleop.status.batteryTooltip", {
        voltage: formatNumber(battery.voltage),
        current: formatNumber(battery.current),
        temperature: formatNumber(battery.temperature, 1),
      })
    : null;

  const speedPercent = derived?.speed_percent ?? null;
  const speedMps = derived?.speed_mps ?? null;
  const velocity = robotState?.velocity;
  const speedTooltip = velocity
    ? t("teleop.status.velocityTooltip", { linear: formatVector(velocity.linear), angular: formatVector(velocity.angular) }) +
      (velocity.frame ? ` ${t("teleop.status.velocityFrame", { frame: velocity.frame })}` : "")
    : null;

  const baseState = robotState?.base_state ?? null;

  const robotTypeLabel =
    robotInfo?.robot_type?.model ?? registeredTypeName(RobotInfo?.robot_type);
  const robotTypeTooltip = [
    robotInfo?.robot_type?.category ? t("teleop.status.category", { category: robotInfo.robot_type.category }) : null,
    robotInfo?.sub_id ? t("teleop.status.subId", { subId: robotInfo.sub_id }) : null,
    robotInfo?.robot_id && robotInfo.robot_id !== RobotInfo?.robot_id
      ? t("teleop.status.robotId", { robotId: robotInfo.robot_id })
      : null,
  ]
    .filter(Boolean)
    .join(" | ");

  const missionState = missionInfo?.mission_state ?? null;
  const missionName = missionInfo?.mission_name ?? null;
  const taskLabel = formatTask(missionInfo?.current_task);

  const pose = robotState?.pose ?? null;
  const yawDeg = derived?.yaw_deg ?? null;

  return (
    <Row gutter={16}>
      <Col offset={1}>
        <div className="robot-status">
          <div className="avatar-container">
            <img src={RobotInfo?.icon_path} className="avatar" alt="" />
          </div>
          <div className="robot-name">{RobotInfo?.robot_id}</div>
          {robotTypeLabel && (
            <Tooltip title={robotTypeTooltip || null}>
              <div className="robot-type">{robotTypeLabel}</div>
            </Tooltip>
          )}
        </div>
      </Col>
      <Col span={4}>
        <Tooltip title={batteryTooltip}>
          <span>
            <BatteryIcon
              Level={derived?.battery_percent ?? null}
              Charging={battery?.is_charging ?? null}
            ></BatteryIcon>
          </span>
        </Tooltip>
        <div className="status-info">
          <Space>
            {t("teleop.status.speed")}{" "}
            {speedPercent == null ? (
              <Tooltip title={t("teleop.status.unknownSpeed")}> (...) </Tooltip>
            ) : (
              <Tooltip title={speedTooltip}>
                <span>
                  <Progress
                    percent={speedPercent}
                    steps={4}
                    status={speedPercent < 88 ? "success" : "exception"}
                    strokeColor={[green[6], green[5], green[4], red[3], red[5]]}
                    format={() => t("teleop.status.speedValue", { speed: formatNumber(speedMps) })}
                  ></Progress>
                </span>
              </Tooltip>
            )}
          </Space>
        </div>
        <div className="status-info">
          <Space>
            {t("teleop.status.state")}
            {baseState ?? <Tooltip title={t("teleop.status.unknownState")}> (...) </Tooltip>}
          </Space>
        </div>
      </Col>

      {ConnectionStatus && <Col>{ConnectionStatus}</Col>}

      <Col>
        {(missionState || missionName) && (
          <div className="status-info">
            <Space>
              {t("teleop.status.mission")}
              {missionName ? `${missionName} (${missionState ?? "-"})` : missionState}
            </Space>
            {taskLabel && <div>{t("teleop.status.task", { task: taskLabel })}</div>}
          </div>
        )}
        {pose && (
          <div className="status-info">
            <Tooltip title={t("teleop.status.poseZ", { z: formatNumber(pose.z) })}>
              <Space>
                {t("teleop.status.pose")}
                {`x ${formatNumber(pose.x)}  y ${formatNumber(pose.y)}`}
                {yawDeg != null && `θ ${formatNumber(yawDeg, 1)}°`}
              </Space>
            </Tooltip>
          </div>
        )}
      </Col>

      {children && <Col>{children}</Col>}
    </Row>
  );
};

export default RobotStatusPanel;
